/**
 * RecordButton — Circular microphone button that toggles recording.
 *
 * Tri-state visual:
 *  - idle: grey microphone icon
 *  - recording: pulsing red with stop icon
 *  - disabled: muted appearance (e.g., while saving)
 */

import { cn } from "../../utils/cn";

interface RecordButtonProps {
  readonly isRecording: boolean;
  readonly disabled?: boolean;
  readonly onToggle: () => void;
}

export function RecordButton({
  isRecording,
  disabled = false,
  onToggle,
}: RecordButtonProps) {
  const label = isRecording ? "Stop recording" : "Start recording";
  const stateClass = isRecording ? "fb-record-btn--active" : "";

  return (
    <button
      type="button"
      className={cn("fb-record-btn", stateClass)}
      onClick={onToggle}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {isRecording ? (
        /* Stop icon — square */
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <rect x="4" y="4" width="12" height="12" rx="2" />
        </svg>
      ) : (
        /* Microphone icon */
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10 1a3 3 0 0 0-3 3v5a3 3 0 1 0 6 0V4a3 3 0 0 0-3-3Z" />
          <path d="M5 9a1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V18H7a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.07A7 7 0 0 0 17 9a1 1 0 1 0-2 0 5 5 0 0 1-10 0Z" />
        </svg>
      )}
    </button>
  );
}
